/**
 * "What would happen if": pick a document type and a department and see the
 * chain such a document would follow, from the resolver the submission uses.
 * How an administrator checks their routing without submitting anything.
 */

import { useState } from 'react';
import { RoutePreview } from './RoutePreview';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

interface Named { id: string; name: string }

export function RouteWhatIf({ types, departments }: { types: readonly Named[]; departments: readonly Named[] }) {
  const [typeId, setTypeId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [asked, setAsked] = useState<{ documentTypeId: string | null; departmentId: string | null } | null>(null);

  return (
    <section className={styles.section} aria-labelledby="whatif-title">
      <h2 className={styles.sectionTitle} id="whatif-title">Which workflow is the default for a document?</h2>
      <p className={own.itemMeta}>
        Every document has one default, chosen by its document type and department: the most specific
        workflow that covers it. An author may pick a different one only when the answer is the
        catch-all, and must say why; administrators may override any route.
        Choose a kind of document to see its default. Nothing is submitted.
      </p>
      <div className={own.addRow}>
        <div className={own.addField}>
          <label className={own.label} htmlFor="whatif-type">Document type</label>
          <select id="whatif-type" className={own.select} value={typeId} onChange={(e) => { setTypeId(e.target.value); setAsked(null); }}>
            <option value="">No document type</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className={own.addField}>
          <label className={own.label} htmlFor="whatif-department">Department</label>
          <select id="whatif-department" className={own.select} value={departmentId} onChange={(e) => { setDepartmentId(e.target.value); setAsked(null); }}>
            <option value="">No department</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <button type="button" className={styles.btn} onClick={() => setAsked({ documentTypeId: typeId || null, departmentId: departmentId || null })}>
          Show the default
        </button>
      </div>
      <RoutePreview ask={asked} heading="By default this document would go to" showChoices />
    </section>
  );
}
